/**
 * Product Card Component
 * Mobile card view for product items with status bar, image, and quick actions
 * Follows scheduled-orders mobile card pattern
 */

import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import { MenuModule } from "primeng/menu";
import { FormsModule } from "@angular/forms";
import { MenuItem } from "primeng/api";
import { IProductSummary } from "../../models";

export interface IProductCardAction {
  action: "view" | "edit" | "syncPrices" | "syncSalePrices" | "delete";
  product: IProductSummary;
}

@Component({
  selector: "app-product-card",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    TagModule,
    TooltipModule,
    MenuModule,
  ],
  templateUrl: "./product-card.component.html",
  styleUrl: "./product-card.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductCardComponent {
  @Input() product!: IProductSummary;
  @Input() selected = false;
  @Input() loading = false;
  @Input() showCheckbox = true;
  @Input() expanded = false;

  @Output() selectionChange = new EventEmitter<boolean>();
  @Output() cardAction = new EventEmitter<IProductCardAction>();
  @Output() cardClick = new EventEmitter<IProductSummary>();

  // Context menu items
  contextMenuItems: MenuItem[] = [];

  /**
   * Get the status class for the card's left border color
   */
  getStatusClass(): string {
    if (!this.product.Active) {
      return "status-inactive";
    }
    // Check for low stock or out of stock
    if (this.product.StockLevel === "OutOfStock") {
      return "status-out-of-stock";
    }
    if (this.product.StockLevel === "LowStock") {
      return "status-low-stock";
    }
    return "status-active";
  }

  /**
   * Get stock badge class
   */
  getStockClass(): string {
    switch (this.product.StockLevel) {
      case "InStock":
        return "in-stock";
      case "LowStock":
        return "low-stock";
      case "OutOfStock":
        return "out-of-stock";
      default:
        return "";
    }
  }

  /**
   * Get stock badge text
   */
  getStockText(): string {
    switch (this.product.StockLevel) {
      case "InStock":
        return "In Stock";
      case "LowStock":
        return "Low Stock";
      case "OutOfStock":
        return "Out of Stock";
      default:
        return this.product.StockLevel || "Unknown";
    }
  }

  /**
   * Get status tag severity
   */
  getStatusSeverity(): "success" | "secondary" | "warn" | "danger" | undefined {
    if (!this.product.Active) {
      return "secondary";
    }
    return "success";
  }

  /**
   * Get status text
   */
  getStatusText(): string {
    return this.product.Active ? "Active" : "Inactive";
  }

  /**
   * Format price with currency
   */
  formatPrice(price: number | undefined | null): string {
    if (price === null || price === undefined) {
      return "N/A";
    }
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  }

  /**
   * Handle checkbox change
   */
  onCheckboxChange(): void {
    this.selectionChange.emit(this.selected);
  }

  /**
   * Handle card body click (to expand/collapse)
   */
  onCardClick(): void {
    this.cardClick.emit(this.product);
  }

  /**
   * Handle view action
   */
  onView(event: Event): void {
    event.stopPropagation();
    this.cardAction.emit({ action: "view", product: this.product });
  }

  /**
   * Handle edit action
   */
  onEdit(event: Event): void {
    event.stopPropagation();
    this.cardAction.emit({ action: "edit", product: this.product });
  }

  /**
   * Handle sync prices action
   */
  onSyncPrices(event: Event): void {
    event.stopPropagation();
    this.cardAction.emit({ action: "syncPrices", product: this.product });
  }

  /**
   * Handle sync sale prices action
   */
  onSyncSalePrices(event: Event): void {
    event.stopPropagation();
    this.cardAction.emit({ action: "syncSalePrices", product: this.product });
  }

  /**
   * Handle more actions menu
   */
  onMoreActions(event: Event, menu: { toggle: (event: Event) => void }): void {
    event.stopPropagation();
    this.buildContextMenu();
    menu.toggle(event);
  }

  /**
   * Build context menu items
   */
  private buildContextMenu(): void {
    this.contextMenuItems = [
      {
        label: "View Details",
        icon: "pi pi-eye",
        command: () =>
          this.cardAction.emit({ action: "view", product: this.product }),
      },
      {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () =>
          this.cardAction.emit({ action: "edit", product: this.product }),
      },
      { separator: true },
      {
        label: "Sync Prices",
        icon: "pi pi-sync",
        command: () =>
          this.cardAction.emit({ action: "syncPrices", product: this.product }),
      },
      {
        label: "Sync Sale Prices",
        icon: "pi pi-sync",
        command: () =>
          this.cardAction.emit({
            action: "syncSalePrices",
            product: this.product,
          }),
      },
      { separator: true },
      {
        label: "Delete",
        icon: "pi pi-trash",
        command: () =>
          this.cardAction.emit({ action: "delete", product: this.product }),
      },
    ];
  }
}
